import React, { useState, useEffect, useRef } from 'react';
import { messages as messagesAPI } from '../../services/api';
import './ChatView.css';

function ChatView({ conversation, onBack, onMessageSent }) {
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  // Fetch conversation messages
  useEffect(() => {
    if (conversation?.otherUserId) {
      fetchMessages();
    }
  }, [conversation?.otherUserId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await messagesAPI.getConversation(
        conversation.otherUserId
      );
      setMessages(response.data.messages || []);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
      // If this is a new conversation (404), that's ok - just empty messages
      if (err.response?.status === 404 || err.response?.status === 400) {
        setMessages([]);
      } else {
        setError('Failed to load messages. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!messageText.trim()) {
      return;
    }

    if (messageText.trim().length > 5000) {
      alert('Message is too long. Maximum 5000 characters.');
      return;
    }

    setSending(true);
    setError(null);

    try {
      const response = await messagesAPI.send({
        receiverId: conversation.otherUserId,
        content: messageText.trim(),
      });

      // Add the new message to the list
      setMessages([...messages, response.data]);
      setMessageText('');

      // Notify parent to refresh conversation list
      if (onMessageSent) {
        onMessageSent();
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to send message. Please try again.');
      }
    } finally {
      setSending(false);
    }
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes < 10 ? '0' + minutes : minutes;
    return `${displayHours}:${displayMinutes} ${ampm}`;
  };

  const formatDateDivider = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    }
  };

  const shouldShowDateDivider = (currentMessage, previousMessage) => {
    if (!previousMessage) return true;

    const currentDate = new Date(currentMessage.createdAt).toDateString();
    const previousDate = new Date(previousMessage.createdAt).toDateString();

    return currentDate !== previousDate;
  };

  return (
    <div className="chat-view">
      <div className="chat-header">
        <button className="back-button-mobile" onClick={onBack}>
          ←
        </button>
        <div className="chat-avatar">
          {conversation.otherUserName?.charAt(0).toUpperCase() || '?'}
        </div>
        <h2 className="chat-username">{conversation.otherUserName}</h2>
      </div>

      <div className="chat-messages">
        {loading && (
          <div className="loading-state">
            <p>Loading messages...</p>
          </div>
        )}

        {error && (
          <div className="error-state">
            <p>{error}</p>
          </div>
        )}

        {!loading && messages.length === 0 && (
          <div className="empty-messages">
            <p>No messages yet. Start the conversation!</p>
          </div>
        )}

        {!loading &&
          messages.map((message, index) => (
            <React.Fragment key={message.id}>
              {shouldShowDateDivider(
                message,
                index > 0 ? messages[index - 1] : null
              ) && (
                <div className="date-divider">
                  {formatDateDivider(message.createdAt)}
                </div>
              )}
              <div
                className={`message-bubble ${
                  message.senderId === conversation.otherUserId
                    ? 'received'
                    : 'sent'
                }`}
              >
                <div className="message-content">{message.content}</div>
                <div className="message-time">
                  {formatMessageTime(message.createdAt)}
                  {message.senderId !== conversation.otherUserId &&
                    message.readAt && <span className="read-indicator">✓✓</span>}
                </div>
              </div>
            </React.Fragment>
          ))}
        <div ref={messagesEndRef} />
      </div>

      <form className="chat-input" onSubmit={handleSendMessage}>
        <textarea
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage(e);
            }
          }}
          placeholder="Type a message..."
          rows={1}
          disabled={sending}
          maxLength={5000}
        />
        <button type="submit" disabled={sending || !messageText.trim()}>
          {sending ? 'Sending...' : 'Send'}
        </button>
      </form>
    </div>
  );
}

export default ChatView;
